export const TEAM_SKILLS_PROTOCOL_VERSION = "v1" as const;
export const TEAM_SKILLS_LEGACY_PROTOCOL_VERSION = "draft-v0" as const;
export const TEAM_SKILLS_MANIFEST_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_CAPABILITY_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_RELEASE_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_SIGNED_ENVELOPE_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_FEED_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_INSTALL_DECISION_SCHEMA_VERSION = 1 as const;
export const TEAM_SKILLS_SIGNATURE_DOMAIN = "doorway.team-skills.signature.v1" as const;

export const TEAM_SKILLS_MAX_FILES = 1_000;
export const TEAM_SKILLS_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const TEAM_SKILLS_MAX_PACKAGE_BYTES = 100 * 1024 * 1024;
export const TEAM_SKILLS_MAX_PATH_BYTES = 4_096;

// The v1 wire contract leaves room for future resumable finalization. The current
// Worker admits a smaller package that can be finalized in one bounded request.
export const TEAM_SKILLS_V1_UPLOAD_MAX_FILES = 100;
export const TEAM_SKILLS_V1_UPLOAD_MAX_PACKAGE_BYTES = 16 * 1024 * 1024;
export const TEAM_SKILLS_V1_NEGOTIATE_D1_STATEMENT_BUDGET = 620;

export type TeamSkillFileKind = "file" | "directory" | "symlink" | "hardlink" | "special";
export type TeamSkillDetectedType = "text" | "asset" | "script" | "binary" | "native" | "secret" | "unknown";
export type TeamSkillFileMode = "0644" | "0755";

export interface TeamSkillFileCandidate {
  path: string;
  kind: TeamSkillFileKind;
  size: number;
  sha256: string;
  mode: string;
  detectedType: TeamSkillDetectedType;
}

export interface CanonicalTeamSkillFile {
  path: string;
  size: number;
  sha256: string;
  mode: TeamSkillFileMode;
}

export interface CanonicalTeamSkillManifest {
  schemaVersion: typeof TEAM_SKILLS_MANIFEST_SCHEMA_VERSION;
  protocolVersion: typeof TEAM_SKILLS_PROTOCOL_VERSION;
  files: CanonicalTeamSkillFile[];
}

export interface TeamSkillMetadata {
  name: string;
  description: string;
  license?: string;
  compatibility?: string;
  metadata?: Record<string, string>;
  allowedTools?: string[];
}

export const TEAM_SKILL_CAPABILITY_LIST_FIELDS = [
  "declaredTools",
  "shellTools",
  "networkTools",
  "externalMcpTools",
  "executableFiles",
  "scripts",
  "compatibility",
  "externalDependencies",
  "outboundDestinations",
  "binaryFiles",
  "unknownCapabilities",
] as const;
export type TeamSkillCapabilityListField = (typeof TEAM_SKILL_CAPABILITY_LIST_FIELDS)[number];
export type TeamSkillCapabilityClassification = "content_only" | "privileged";

export interface TeamSkillCapabilityEnvelope {
  schemaVersion: typeof TEAM_SKILLS_CAPABILITY_SCHEMA_VERSION;
  protocolVersion: typeof TEAM_SKILLS_PROTOCOL_VERSION;
  scannerVersion: string;
  classification: TeamSkillCapabilityClassification;
  declaredTools: string[];
  shellTools: string[];
  networkTools: string[];
  externalMcpTools: string[];
  executableFiles: string[];
  scripts: string[];
  compatibility: string[];
  externalDependencies: string[];
  outboundDestinations: string[];
  binaryFiles: string[];
  unknownCapabilities: string[];
}

export interface TeamSkillCapabilityDiff {
  schemaVersion: 1;
  protocolVersion: typeof TEAM_SKILLS_PROTOCOL_VERSION;
  fromClassification: TeamSkillCapabilityClassification;
  toClassification: TeamSkillCapabilityClassification;
  changed: boolean;
  added: Record<TeamSkillCapabilityListField, string[]>;
  removed: Record<TeamSkillCapabilityListField, string[]>;
}

export interface ComparableTeamSkillFile {
  path: string;
  sha256: string;
  mode: TeamSkillFileMode;
  byteLength: number;
  bytes?: Uint8Array;
}

export type TeamSkillFileChangeKind = "added" | "updated" | "removed" | "unchanged";
export type TeamSkillDiffLineKind = "context" | "added" | "removed";

export interface TeamSkillFileComparison {
  path: string;
  kind: TeamSkillFileChangeKind;
  before: { sha256: string; mode: TeamSkillFileMode; byteLength: number } | null;
  after: { sha256: string; mode: TeamSkillFileMode; byteLength: number } | null;
  textDiff: null | {
    lines: Array<{ kind: TeamSkillDiffLineKind; text: string }>;
    truncated: boolean;
  };
}

export interface TeamSkillVersionComparison {
  files: TeamSkillFileComparison[];
  counts: Record<TeamSkillFileChangeKind, number>;
  truncated: boolean;
}

export interface TeamSkillComparisonLimits {
  maxComparedFiles?: number;
  maxTextBytesPerFile?: number;
  maxInputLinesPerFile?: number;
  maxOutputLinesPerFile?: number;
  contextLines?: number;
}

export type TeamSkillsContractErrorCode =
  | "INVALID_CONTRACT"
  | "UNSUPPORTED_SCHEMA_VERSION"
  | "UNSUPPORTED_PROTOCOL_VERSION"
  | "UNSAFE_PATH"
  | "PATH_COLLISION"
  | "UNSUPPORTED_FILE_TYPE"
  | "INVALID_DIGEST"
  | "INVALID_MODE"
  | "MISSING_SKILL_MD"
  | "FORBIDDEN_PACKAGE_SHAPE"
  | "SECRET_DETECTED"
  | "NATIVE_BINARY_REJECTED"
  | "FILE_COUNT_LIMIT"
  | "FILE_SIZE_LIMIT"
  | "PACKAGE_SIZE_LIMIT"
  | "INVALID_SKILL_METADATA"
  | "CAPABILITY_CLASSIFICATION_MISMATCH"
  | "CAPABILITY_MANIFEST_MISMATCH"
  | "MISSING_TOOL_DECLARATION"
  | "DUPLICATE_SKILL_ID"
  | "IDENTITY_MISMATCH"
  | "INVALID_GENERATION"
  | "INVALID_TIME_RANGE";

export class TeamSkillsContractError extends Error {
  readonly code: TeamSkillsContractErrorCode;

  constructor(code: TeamSkillsContractErrorCode, message: string) {
    super(message);
    this.name = "TeamSkillsContractError";
    this.code = code;
  }
}

export type TeamSkillsProtocolCompatibility =
  | { outcome: "compatible"; requestedProtocolVersion: "v1"; protocolVersion: "v1" }
  | { outcome: "upgrade_required"; requestedProtocolVersion: "draft-v0"; protocolVersion: "v1"; reasonCode: "DRAFT_PROTOCOL_RETIRED" }
  | { outcome: "unsupported"; requestedProtocolVersion: string | null; protocolVersion: "v1"; reasonCode: "UNSUPPORTED_PROTOCOL_VERSION" };

export interface ReleaseActivationDecision {
  schemaVersion: 1;
  protocolVersion: typeof TEAM_SKILLS_PROTOCOL_VERSION;
  status: "activate" | "pending_owner_approval";
  requiredRole: "creator" | "owner";
  workspaceId: string;
  skillId: string;
  versionId: string;
  manifestDigest: string;
  capabilityDigest: string;
  scannerVersion: string;
  baselineReleaseGeneration: number;
}

export interface P256PublicJwk {
  kty: "EC";
  crv: "P-256";
  x: string;
  y: string;
}

export interface DelegatedSigningKeyPayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "delegated_key";
  rootVersion: number;
  delegationGeneration: number;
  rootKeyId: string;
  signingKeyId: string;
  algorithm: "ES256";
  publicKeyJwk: P256PublicJwk;
  status: "active" | "revoked";
  revokedAt: string | null;
  issuedAt: string;
  notBefore: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export interface TeamSkillsTrustBootstrap {
  schemaVersion: 1;
  protocolVersion: "v1";
  workspaceId: string;
  minimumClientVersion: string;
  delegations: SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload>[];
}

export interface TeamSkillReleasePayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "release";
  signingKeyId: string;
  workspaceId: string;
  skillId: string;
  versionId: string;
  versionNumber: number;
  manifestDigest: string;
  capabilityDigest: string;
  manifest: CanonicalTeamSkillManifest;
  capability: TeamSkillCapabilityEnvelope;
  scannerVersion: string;
  releaseGeneration: number;
  feedGeneration: number;
  lifecycle: "active";
  locked: boolean;
  issuedAt: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export interface TeamSkillTombstonePayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "tombstone";
  signingKeyId: string;
  workspaceId: string;
  skillId: string;
  releaseGeneration: number;
  feedGeneration: number;
  tombstoneGeneration: number;
  previousManifestDigest: string;
  lifecycle: "removed";
  locked: boolean;
  removedAt: string;
  issuedAt: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export type TerminalAuthorityReason = "skill_disabled" | "skill_removed" | "access_ended" | "membership_revoked" | "device_revoked";

export interface TeamSkillTerminalAuthorityPayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "terminal_authority";
  signingKeyId: string;
  workspaceId: string;
  skillId: string | null;
  deviceId: string;
  authorityGeneration: number;
  feedGeneration: number;
  reason: TerminalAuthorityReason;
  lifecycle: "disabled" | "removed";
  locked: true;
  issuedAt: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export interface TeamSkillStatePayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "state";
  signingKeyId: string;
  workspaceId: string;
  skillId: string;
  versionId: string | null;
  manifestDigest: string | null;
  capabilityDigest: string | null;
  releaseGeneration: number;
  stateGeneration: number;
  feedGeneration: number;
  lifecycle: "active" | "disabled";
  locked: boolean;
  issuedAt: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export interface TeamSkillsFeedCheckpointPayload {
  schemaVersion: 1;
  protocolVersion: "v1";
  kind: "feed_checkpoint";
  signingKeyId: string;
  workspaceId: string;
  requestedCursor: number;
  fromCursor: number;
  toCursor: number;
  currentCursor: number;
  hasMore: boolean;
  outcome: "events" | "cursor_reset_required";
  eventDigest: string;
  issuedAt: string;
  expiresAt: string;
  minimumClientVersion: string;
}

export type TeamSkillsSignablePayload =
  | DelegatedSigningKeyPayload
  | TeamSkillReleasePayload
  | TeamSkillStatePayload
  | TeamSkillTombstonePayload
  | TeamSkillTerminalAuthorityPayload
  | TeamSkillsFeedCheckpointPayload;

export interface SignedTeamSkillsEnvelope<Payload> {
  schemaVersion: 1;
  protocolVersion: "v1";
  keyId: string;
  algorithm: "ES256";
  payload: Payload;
  signature: string;
}

const encoder = new TextEncoder();
const digestPattern = /^[a-f0-9]{64}$/;
const base64UrlCoordinatePattern = /^[A-Za-z0-9_-]{43}$/;
const base64UrlP256SignaturePattern = /^[A-Za-z0-9_-]{86}$/;
const semverPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?$/;
const forbiddenPathCodePoints = /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/u;

function fail(code: TeamSkillsContractErrorCode, message: string): never {
  throw new TeamSkillsContractError(code, message);
}

function compareUtf8(left: string, right: string): number {
  const leftBytes = encoder.encode(left);
  const rightBytes = encoder.encode(right);
  const length = Math.min(leftBytes.length, rightBytes.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftBytes[index] ?? 0) - (rightBytes[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return leftBytes.length - rightBytes.length;
}

function recordAt(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) fail("INVALID_CONTRACT", `${path} must be an object.`);
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) fail("INVALID_CONTRACT", `${path} must be a plain object.`);
  return value as Record<string, unknown>;
}

function exactKeys(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const allowedSet = new Set(allowed);
  const unexpected = Object.keys(record).find((key) => !allowedSet.has(key));
  if (unexpected !== undefined) fail("INVALID_CONTRACT", `${path}.${unexpected} is not allowed.`);
}

function requiredKeys(record: Record<string, unknown>, required: readonly string[], path: string): void {
  const missing = required.find((key) => !Object.hasOwn(record, key));
  if (missing !== undefined) fail("INVALID_CONTRACT", `${path}.${missing} is required.`);
}

function stringAt(value: unknown, path: string, maximum = 512): string {
  if (typeof value !== "string" || value.trim().length === 0 || value.length > maximum) {
    fail("INVALID_CONTRACT", `${path} must be a non-empty string no longer than ${maximum} characters.`);
  }
  return value;
}

function identifierAt(value: unknown, path: string): string {
  const identifier = stringAt(value, path, 200);
  if (forbiddenPathCodePoints.test(identifier)) fail("INVALID_CONTRACT", `${path} contains forbidden characters.`);
  return identifier;
}

function digestAt(value: unknown, path: string): string {
  if (typeof value !== "string" || !digestPattern.test(value)) fail("INVALID_DIGEST", `${path} must be a lowercase SHA-256 digest.`);
  return value;
}

function integerAt(value: unknown, path: string, minimum = 0): number {
  if (!Number.isSafeInteger(value) || (value as number) < minimum) fail("INVALID_GENERATION", `${path} must be a safe integer of at least ${minimum}.`);
  return value as number;
}

function timestampAt(value: unknown, path: string): string {
  const timestamp = stringAt(value, path);
  if (!Number.isFinite(Date.parse(timestamp)) || new Date(timestamp).toISOString() !== timestamp) {
    fail("INVALID_CONTRACT", `${path} must be a canonical ISO timestamp.`);
  }
  return timestamp;
}

function validateTimeRange(issuedAt: string, expiresAt: string): void {
  if (Date.parse(expiresAt) <= Date.parse(issuedAt)) fail("INVALID_TIME_RANGE", "Signed metadata expiry must follow issue time.");
}

function validateSchema(record: Record<string, unknown>, path: string, expected = 1): void {
  if (record.schemaVersion !== expected) fail("UNSUPPORTED_SCHEMA_VERSION", `${path}.schemaVersion must equal ${expected}.`);
}

function validateProtocol(value: unknown): void {
  if (value !== TEAM_SKILLS_PROTOCOL_VERSION) fail("UNSUPPORTED_PROTOCOL_VERSION", `protocolVersion must equal ${TEAM_SKILLS_PROTOCOL_VERSION}.`);
}

function enumAt<const Values extends readonly string[]>(value: unknown, values: Values, path: string): Values[number] {
  if (typeof value !== "string" || !values.includes(value)) fail("INVALID_CONTRACT", `${path} has an unsupported value.`);
  return value as Values[number];
}

function normalizePortablePath(value: unknown): string {
  if (typeof value !== "string" || value.length === 0 || value.includes("\\") || value.startsWith("/") || /^[A-Za-z]:/.test(value)) {
    fail("UNSAFE_PATH", "Skill path is unsafe.");
  }
  const normalized = value.normalize("NFC");
  if (encoder.encode(normalized).byteLength > TEAM_SKILLS_MAX_PATH_BYTES || forbiddenPathCodePoints.test(normalized)) {
    fail("UNSAFE_PATH", "Skill path is unsafe.");
  }
  const segments = normalized.split("/");
  if (segments.some((segment) => segment.length === 0 || segment === "." || segment === "..")) fail("UNSAFE_PATH", "Skill path is unsafe.");
  return normalized;
}

function collisionKey(path: string): string {
  return path.normalize("NFC").toLowerCase();
}

function rejectForbiddenShape(path: string, detectedType: TeamSkillDetectedType): void {
  const lower = path.toLowerCase();
  const segments = lower.split("/");
  const basename = segments.at(-1) ?? "";
  if (
    lower === ".mcp.json"
    || lower === "mcp.json"
    || lower.startsWith(".claude-plugin/")
    || lower === "hooks/hooks.json"
    || lower === ".claude/hooks.json"
    || (segments.includes("mcp") && basename === "package.json")
  ) fail("FORBIDDEN_PACKAGE_SHAPE", "Embedded plugin, hook, or MCP package configuration is not accepted.");
  if (/\.(?:dylib|so|dll|exe|msi|pkg|dmg|kext|node)$/i.test(path) || segments.some((segment) => /\.(?:app|framework|bundle)$/.test(segment))) {
    fail("NATIVE_BINARY_REJECTED", "Native packages and loadable binaries are not accepted.");
  }
  if (detectedType === "secret" || /^(?:\.env(?:\..+)?|id_(?:rsa|dsa|ecdsa|ed25519)|credentials\.json)$/i.test(basename) || /\.(?:pem|p12|pfx|key)$/i.test(basename)) {
    fail("SECRET_DETECTED", "Secret-bearing skill content is not accepted.");
  }
  if (detectedType === "native") fail("NATIVE_BINARY_REJECTED", "Native packages and loadable binaries are not accepted.");
}

function normalizeCandidateMode(mode: string, path: string, detectedType: TeamSkillDetectedType): TeamSkillFileMode {
  if (!/^[0-7]{4}$/.test(mode)) fail("INVALID_MODE", "Skill file mode must be a four-digit octal string.");
  const permissionBits = Number.parseInt(mode, 8) & 0o777;
  const hasExecutableBit = (permissionBits & 0o111) !== 0;
  if (detectedType === "script") {
    if (!path.startsWith("scripts/")) fail("INVALID_MODE", "Approved scripts must be stored under scripts/.");
    return "0755";
  }
  if (detectedType === "unknown" && hasExecutableBit) {
    fail("UNSUPPORTED_FILE_TYPE", "Unknown executable formats are not accepted.");
  }
  return "0644";
}

export function canonicalizeSkillManifest(files: readonly TeamSkillFileCandidate[]): CanonicalTeamSkillManifest {
  if (files.length > TEAM_SKILLS_MAX_FILES) fail("FILE_COUNT_LIMIT", `A skill may contain at most ${TEAM_SKILLS_MAX_FILES} files.`);
  const paths = new Map<string, string>();
  const canonicalFiles: CanonicalTeamSkillFile[] = [];
  let totalBytes = 0;
  for (const candidate of files) {
    if (candidate.kind !== "file") fail("UNSUPPORTED_FILE_TYPE", "Only regular files are accepted in a skill bundle.");
    const path = normalizePortablePath(candidate.path);
    const key = collisionKey(path);
    if (paths.has(key)) fail("PATH_COLLISION", "Skill paths collide after NFC and case normalization.");
    paths.set(key, path);
    if (!Number.isSafeInteger(candidate.size) || candidate.size < 0 || candidate.size > TEAM_SKILLS_MAX_FILE_BYTES) {
      fail("FILE_SIZE_LIMIT", `A skill file may contain at most ${TEAM_SKILLS_MAX_FILE_BYTES} bytes.`);
    }
    totalBytes += candidate.size;
    if (!Number.isSafeInteger(totalBytes) || totalBytes > TEAM_SKILLS_MAX_PACKAGE_BYTES) {
      fail("PACKAGE_SIZE_LIMIT", `A skill package may contain at most ${TEAM_SKILLS_MAX_PACKAGE_BYTES} bytes.`);
    }
    if (!digestPattern.test(candidate.sha256)) fail("INVALID_DIGEST", "Skill file digests must be lowercase SHA-256 values.");
    const mode = normalizeCandidateMode(candidate.mode, path, candidate.detectedType);
    rejectForbiddenShape(path, candidate.detectedType);
    canonicalFiles.push({ path, size: candidate.size, sha256: candidate.sha256, mode });
  }
  canonicalFiles.sort((left, right) => compareUtf8(left.path, right.path));
  const skillFile = canonicalFiles.find((file) => file.path === "SKILL.md");
  if (!skillFile) fail("MISSING_SKILL_MD", "A standard skill requires one root SKILL.md file.");
  if (skillFile.mode !== "0644") fail("INVALID_MODE", "SKILL.md must use normalized mode 0644.");
  return { schemaVersion: TEAM_SKILLS_MANIFEST_SCHEMA_VERSION, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION, files: canonicalFiles };
}

export function canonicalTeamSkillsJson(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number" && Number.isSafeInteger(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalTeamSkillsJson).join(",")}]`;
  if (typeof value === "object") {
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) fail("INVALID_CONTRACT", "Canonical Team Skills JSON accepts plain objects only.");
    const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => compareUtf8(left, right));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalTeamSkillsJson(entry)}`).join(",")}}`;
  }
  fail("INVALID_CONTRACT", "Canonical Team Skills JSON accepts JSON-safe values only.");
}

async function sha256Canonical(value: unknown): Promise<string> {
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", encoder.encode(canonicalTeamSkillsJson(value))));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function digestSkillManifest(manifest: CanonicalTeamSkillManifest): Promise<string> {
  parseCanonicalSkillManifest(manifest);
  return sha256Canonical(manifest);
}

export function parseCanonicalSkillManifest(value: unknown): CanonicalTeamSkillManifest {
  const manifest = recordAt(value, "manifest");
  exactKeys(manifest, ["schemaVersion", "protocolVersion", "files"], "manifest");
  requiredKeys(manifest, ["schemaVersion", "protocolVersion", "files"], "manifest");
  validateSchema(manifest, "manifest", TEAM_SKILLS_MANIFEST_SCHEMA_VERSION);
  validateProtocol(manifest.protocolVersion);
  if (!Array.isArray(manifest.files)) fail("INVALID_CONTRACT", "manifest.files must be an array.");
  const candidateFiles = manifest.files.map((entry, index): TeamSkillFileCandidate => {
    const file = recordAt(entry, `manifest.files[${index}]`);
    exactKeys(file, ["path", "size", "sha256", "mode"], `manifest.files[${index}]`);
    requiredKeys(file, ["path", "size", "sha256", "mode"], `manifest.files[${index}]`);
    return {
      path: String(file.path), kind: "file", size: file.size as number, sha256: String(file.sha256), mode: String(file.mode),
      detectedType: String(file.mode) === "0755" ? "script" : "text",
    };
  });
  const canonical = canonicalizeSkillManifest(candidateFiles);
  if (canonicalTeamSkillsJson(canonical) !== canonicalTeamSkillsJson(manifest)) fail("INVALID_CONTRACT", "manifest must use exact canonical ordering and normalized values.");
  return manifest as unknown as CanonicalTeamSkillManifest;
}

export function parseSkillMetadata(value: unknown, expectedDirectoryName: string): TeamSkillMetadata {
  const metadata = recordAt(value, "skillMetadata");
  exactKeys(metadata, ["name", "description", "license", "compatibility", "metadata", "allowedTools"], "skillMetadata");
  requiredKeys(metadata, ["name", "description"], "skillMetadata");
  const name = stringAt(metadata.name, "skillMetadata.name", 64);
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name) || name !== expectedDirectoryName) {
    fail("INVALID_SKILL_METADATA", "Skill name must follow the Agent Skills naming rules and match its parent directory.");
  }
  stringAt(metadata.description, "skillMetadata.description", 1024);
  if (metadata.license !== undefined) stringAt(metadata.license, "skillMetadata.license", 500);
  if (metadata.compatibility !== undefined) stringAt(metadata.compatibility, "skillMetadata.compatibility", 500);
  if (metadata.metadata !== undefined) {
    const additional = recordAt(metadata.metadata, "skillMetadata.metadata");
    for (const [key, entry] of Object.entries(additional)) {
      stringAt(key, "skillMetadata.metadata key", 100);
      stringAt(entry, `skillMetadata.metadata.${key}`, 500);
    }
  }
  if (metadata.allowedTools !== undefined) parseCanonicalStringArray(metadata.allowedTools, "skillMetadata.allowedTools", false);
  return metadata as unknown as TeamSkillMetadata;
}

function parseCanonicalStringArray(value: unknown, path: string, pathValues: boolean): string[] {
  if (!Array.isArray(value) || value.length > TEAM_SKILLS_MAX_FILES) fail("INVALID_CONTRACT", `${path} must be a bounded array.`);
  const strings = value.map((entry, index) => {
    const original = stringAt(entry, `${path}[${index}]`, pathValues ? TEAM_SKILLS_MAX_PATH_BYTES : 500);
    if (original.normalize("NFC") !== original) fail("INVALID_CONTRACT", `${path}[${index}] must use NFC normalization.`);
    return pathValues ? normalizePortablePath(original) : original;
  });
  const sorted = [...new Set(strings)].sort(compareUtf8);
  if (canonicalTeamSkillsJson(strings) !== canonicalTeamSkillsJson(sorted)) fail("INVALID_CONTRACT", `${path} must be unique and sorted by UTF-8 bytes.`);
  return strings;
}

function capabilityIsPrivileged(envelope: Pick<TeamSkillCapabilityEnvelope, TeamSkillCapabilityListField>): boolean {
  return envelope.shellTools.length > 0
    || envelope.networkTools.length > 0
    || envelope.externalMcpTools.length > 0
    || envelope.executableFiles.length > 0
    || envelope.scripts.length > 0
    || envelope.externalDependencies.length > 0
    || envelope.outboundDestinations.length > 0
    || envelope.binaryFiles.length > 0
    || envelope.unknownCapabilities.length > 0;
}

export function parseCapabilityEnvelope(value: unknown): TeamSkillCapabilityEnvelope {
  const envelope = recordAt(value, "capability");
  const keys = ["schemaVersion", "protocolVersion", "scannerVersion", "classification", ...TEAM_SKILL_CAPABILITY_LIST_FIELDS] as const;
  exactKeys(envelope, keys, "capability");
  requiredKeys(envelope, keys, "capability");
  validateSchema(envelope, "capability", TEAM_SKILLS_CAPABILITY_SCHEMA_VERSION);
  validateProtocol(envelope.protocolVersion);
  identifierAt(envelope.scannerVersion, "capability.scannerVersion");
  const classification = enumAt(envelope.classification, ["content_only", "privileged"] as const, "capability.classification");
  const parsed = {} as Record<TeamSkillCapabilityListField, string[]>;
  for (const field of TEAM_SKILL_CAPABILITY_LIST_FIELDS) {
    const pathValues = ["executableFiles", "scripts", "binaryFiles"].includes(field);
    parsed[field] = parseCanonicalStringArray(envelope[field], `capability.${field}`, pathValues);
  }
  for (const declaredField of ["shellTools", "networkTools", "externalMcpTools"] as const) {
    const missing = parsed[declaredField].find((tool) => !parsed.declaredTools.includes(tool));
    if (missing !== undefined) fail("MISSING_TOOL_DECLARATION", `capability.${declaredField} contains an undeclared tool.`);
  }
  const expected = capabilityIsPrivileged(parsed) ? "privileged" : "content_only";
  if (classification !== expected) fail("CAPABILITY_CLASSIFICATION_MISMATCH", `capability.classification must equal ${expected}.`);
  return envelope as unknown as TeamSkillCapabilityEnvelope;
}

export async function digestCapabilityEnvelope(envelope: TeamSkillCapabilityEnvelope): Promise<string> {
  parseCapabilityEnvelope(envelope);
  return sha256Canonical(envelope);
}

export function diffCapabilityEnvelopes(from: TeamSkillCapabilityEnvelope, to: TeamSkillCapabilityEnvelope): TeamSkillCapabilityDiff {
  const before = parseCapabilityEnvelope(from);
  const after = parseCapabilityEnvelope(to);
  const added = {} as Record<TeamSkillCapabilityListField, string[]>;
  const removed = {} as Record<TeamSkillCapabilityListField, string[]>;
  for (const field of TEAM_SKILL_CAPABILITY_LIST_FIELDS) {
    added[field] = after[field].filter((entry) => !before[field].includes(entry));
    removed[field] = before[field].filter((entry) => !after[field].includes(entry));
  }
  const changed = before.classification !== after.classification
    || TEAM_SKILL_CAPABILITY_LIST_FIELDS.some((field) => added[field].length > 0 || removed[field].length > 0);
  return { schemaVersion: 1, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION, fromClassification: before.classification, toClassification: after.classification, changed, added, removed };
}

export function compareTeamSkillVersions(
  beforeFiles: readonly ComparableTeamSkillFile[],
  afterFiles: readonly ComparableTeamSkillFile[],
  requestedLimits: TeamSkillComparisonLimits = {},
): TeamSkillVersionComparison {
  const limits = {
    maxComparedFiles: clampLimit(requestedLimits.maxComparedFiles, 50, 1, 100),
    maxTextBytesPerFile: clampLimit(requestedLimits.maxTextBytesPerFile, 128 * 1024, 1, 512 * 1024),
    maxInputLinesPerFile: clampLimit(requestedLimits.maxInputLinesPerFile, 500, 1, 2_000),
    maxOutputLinesPerFile: clampLimit(requestedLimits.maxOutputLinesPerFile, 160, 1, 500),
    contextLines: clampLimit(requestedLimits.contextLines, 3, 0, 10),
  };
  const before = new Map(beforeFiles.map((file) => [file.path.normalize("NFC"), file]));
  const after = new Map(afterFiles.map((file) => [file.path.normalize("NFC"), file]));
  const paths = [...new Set([...before.keys(), ...after.keys()])].sort((left, right) =>
    left < right ? -1 : left > right ? 1 : 0);
  const counts: Record<TeamSkillFileChangeKind, number> = { added: 0, updated: 0, removed: 0, unchanged: 0 };
  const all = paths.map((path): TeamSkillFileComparison => {
    const oldFile = before.get(path);
    const newFile = after.get(path);
    const kind: TeamSkillFileChangeKind = !oldFile ? "added"
      : !newFile ? "removed"
        : oldFile.sha256 !== newFile.sha256 || oldFile.mode !== newFile.mode ? "updated"
          : "unchanged";
    counts[kind] += 1;
    const textDiff = kind === "unchanged"
      ? null
      : oldFile?.bytes && newFile?.bytes
        ? boundedTextDiff(oldFile.bytes, newFile.bytes, limits)
        : !oldFile && newFile?.bytes
          ? boundedTextDiff(EMPTY_COMPARISON_BYTES, newFile.bytes, limits)
          : oldFile?.bytes && !newFile
            ? boundedTextDiff(oldFile.bytes, EMPTY_COMPARISON_BYTES, limits)
            : null;
    return {
      path,
      kind,
      before: oldFile ? comparableFileMetadata(oldFile) : null,
      after: newFile ? comparableFileMetadata(newFile) : null,
      textDiff,
    };
  });
  const changed = all.filter((file) => file.kind !== "unchanged");
  return {
    files: changed.slice(0, limits.maxComparedFiles),
    counts,
    truncated: changed.length > limits.maxComparedFiles,
  };
}

const EMPTY_COMPARISON_BYTES = new Uint8Array();

function comparableFileMetadata(file: ComparableTeamSkillFile) {
  return { sha256: file.sha256, mode: file.mode, byteLength: file.byteLength };
}

function boundedTextDiff(
  before: Uint8Array,
  after: Uint8Array,
  limits: Required<TeamSkillComparisonLimits>,
): TeamSkillFileComparison["textDiff"] {
  if (before.byteLength > limits.maxTextBytesPerFile || after.byteLength > limits.maxTextBytesPerFile) return null;
  const oldText = decodeComparableText(before);
  const newText = decodeComparableText(after);
  if (oldText === null || newText === null) return null;
  if (oldText === newText) return { lines: [], truncated: false };
  const oldLines = splitComparisonLines(oldText);
  const newLines = splitComparisonLines(newText);
  if (oldLines.length > limits.maxInputLinesPerFile || newLines.length > limits.maxInputLinesPerFile) return null;
  const matrix = Array.from({ length: oldLines.length + 1 }, () => new Uint16Array(newLines.length + 1));
  for (let oldIndex = oldLines.length - 1; oldIndex >= 0; oldIndex -= 1) {
    for (let newIndex = newLines.length - 1; newIndex >= 0; newIndex -= 1) {
      matrix[oldIndex]![newIndex] = oldLines[oldIndex] === newLines[newIndex]
        ? matrix[oldIndex + 1]![newIndex + 1]! + 1
        : Math.max(matrix[oldIndex + 1]![newIndex]!, matrix[oldIndex]![newIndex + 1]!);
    }
  }
  const raw: Array<{ kind: TeamSkillDiffLineKind; text: string }> = [];
  let oldIndex = 0;
  let newIndex = 0;
  while (oldIndex < oldLines.length || newIndex < newLines.length) {
    if (oldLines[oldIndex] === newLines[newIndex]) {
      raw.push({ kind: "context", text: oldLines[oldIndex]! });
      oldIndex += 1;
      newIndex += 1;
    } else if (newIndex < newLines.length
      && (oldIndex >= oldLines.length || matrix[oldIndex]![newIndex + 1]! > matrix[oldIndex + 1]![newIndex]!)) {
      raw.push({ kind: "added", text: newLines[newIndex]! });
      newIndex += 1;
    } else {
      raw.push({ kind: "removed", text: oldLines[oldIndex]! });
      oldIndex += 1;
    }
  }
  const visible = new Set<number>();
  raw.forEach((line, index) => {
    if (line.kind === "context") return;
    for (let offset = -limits.contextLines; offset <= limits.contextLines; offset += 1) {
      if (index + offset >= 0 && index + offset < raw.length) visible.add(index + offset);
    }
  });
  const lines = [...visible].sort((left, right) => left - right).map((index) => raw[index]!);
  return {
    lines: lines.slice(0, limits.maxOutputLinesPerFile),
    truncated: lines.length > limits.maxOutputLinesPerFile || visible.size < raw.length,
  };
}

function decodeComparableText(bytes: Uint8Array): string | null {
  if (bytes.includes(0)) return null;
  try {
    const value = new TextDecoder("utf-8", { fatal: true, ignoreBOM: false }).decode(bytes);
    return /[\u0001-\u0008\u000b\u000c\u000e-\u001f]/.test(value) ? null : value;
  } catch {
    return null;
  }
}

function splitComparisonLines(value: string): string[] {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  if (lines.at(-1) === "") lines.pop();
  return lines;
}

function clampLimit(value: number | undefined, fallback: number, minimum: number, maximum: number): number {
  return Number.isInteger(value) ? Math.min(maximum, Math.max(minimum, value!)) : fallback;
}

export async function decideSkillPublication(input: {
  workspaceId: string;
  skillId: string;
  versionId: string;
  manifestDigest: string;
  baselineReleaseGeneration: number;
  capability: TeamSkillCapabilityEnvelope;
}): Promise<ReleaseActivationDecision> {
  identifierAt(input.workspaceId, "publication.workspaceId");
  identifierAt(input.skillId, "publication.skillId");
  identifierAt(input.versionId, "publication.versionId");
  digestAt(input.manifestDigest, "publication.manifestDigest");
  integerAt(input.baselineReleaseGeneration, "publication.baselineReleaseGeneration");
  const capability = parseCapabilityEnvelope(input.capability);
  const capabilityDigest = await digestCapabilityEnvelope(capability);
  const privileged = capability.classification === "privileged";
  return {
    schemaVersion: 1, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION,
    status: privileged ? "pending_owner_approval" : "activate",
    requiredRole: privileged ? "owner" : "creator",
    workspaceId: input.workspaceId, skillId: input.skillId, versionId: input.versionId,
    manifestDigest: input.manifestDigest, capabilityDigest,
    scannerVersion: capability.scannerVersion, baselineReleaseGeneration: input.baselineReleaseGeneration,
  };
}

export function parseReleaseActivationDecision(value: unknown): ReleaseActivationDecision {
  const decision = recordAt(value, "releaseDecision");
  const keys = ["schemaVersion", "protocolVersion", "status", "requiredRole", "workspaceId", "skillId", "versionId", "manifestDigest", "capabilityDigest", "scannerVersion", "baselineReleaseGeneration"] as const;
  exactKeys(decision, keys, "releaseDecision"); requiredKeys(decision, keys, "releaseDecision");
  validateSchema(decision, "releaseDecision"); validateProtocol(decision.protocolVersion);
  const status = enumAt(decision.status, ["activate", "pending_owner_approval"] as const, "releaseDecision.status");
  const requiredRole = enumAt(decision.requiredRole, ["creator", "owner"] as const, "releaseDecision.requiredRole");
  if ((status === "activate" && requiredRole !== "creator") || (status === "pending_owner_approval" && requiredRole !== "owner")) {
    fail("CAPABILITY_CLASSIFICATION_MISMATCH", "Release decision role does not match its activation status.");
  }
  identifierAt(decision.workspaceId, "releaseDecision.workspaceId"); identifierAt(decision.skillId, "releaseDecision.skillId");
  identifierAt(decision.versionId, "releaseDecision.versionId"); digestAt(decision.manifestDigest, "releaseDecision.manifestDigest");
  digestAt(decision.capabilityDigest, "releaseDecision.capabilityDigest"); identifierAt(decision.scannerVersion, "releaseDecision.scannerVersion");
  integerAt(decision.baselineReleaseGeneration, "releaseDecision.baselineReleaseGeneration");
  return decision as unknown as ReleaseActivationDecision;
}

function parseP256PublicJwk(value: unknown, path: string): P256PublicJwk {
  const jwk = recordAt(value, path);
  exactKeys(jwk, ["kty", "crv", "x", "y"], path);
  requiredKeys(jwk, ["kty", "crv", "x", "y"], path);
  if (jwk.kty !== "EC" || jwk.crv !== "P-256" || typeof jwk.x !== "string" || typeof jwk.y !== "string"
    || !base64UrlCoordinatePattern.test(jwk.x) || !base64UrlCoordinatePattern.test(jwk.y)) {
    fail("INVALID_CONTRACT", `${path} must contain a public EC P-256 JWK.`);
  }
  return jwk as unknown as P256PublicJwk;
}

function minimumClientVersionAt(value: unknown, path: string): string {
  const version = stringAt(value, path, 100);
  if (!semverPattern.test(version)) fail("INVALID_CONTRACT", `${path} must be a semantic version.`);
  return version;
}

function parseSignedEnvelopeShape(value: unknown, path: string): Record<string, unknown> {
  const envelope = recordAt(value, path);
  exactKeys(envelope, ["schemaVersion", "protocolVersion", "keyId", "algorithm", "payload", "signature"], path);
  requiredKeys(envelope, ["schemaVersion", "protocolVersion", "keyId", "algorithm", "payload", "signature"], path);
  validateSchema(envelope, path, TEAM_SKILLS_SIGNED_ENVELOPE_SCHEMA_VERSION);
  validateProtocol(envelope.protocolVersion);
  digestAt(envelope.keyId, `${path}.keyId`);
  if (envelope.algorithm !== "ES256" || typeof envelope.signature !== "string" || !base64UrlP256SignaturePattern.test(envelope.signature)) {
    fail("INVALID_CONTRACT", `${path} must use an unpadded 64-byte ES256 signature.`);
  }
  return envelope;
}

export function parseDelegatedSigningKeyEnvelope(value: unknown): SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload> {
  const envelope = parseSignedEnvelopeShape(value, "delegation");
  const payload = recordAt(envelope.payload, "delegation.payload");
  const keys = ["schemaVersion", "protocolVersion", "kind", "rootVersion", "delegationGeneration", "rootKeyId", "signingKeyId", "algorithm", "publicKeyJwk", "status", "revokedAt", "issuedAt", "notBefore", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "delegation.payload"); requiredKeys(payload, keys, "delegation.payload");
  validateSchema(payload, "delegation.payload"); validateProtocol(payload.protocolVersion);
  if (payload.kind !== "delegated_key" || payload.algorithm !== "ES256") fail("INVALID_CONTRACT", "delegation.payload has an unsupported kind or algorithm.");
  integerAt(payload.rootVersion, "delegation.payload.rootVersion", 1);
  integerAt(payload.delegationGeneration, "delegation.payload.delegationGeneration", 1);
  digestAt(payload.rootKeyId, "delegation.payload.rootKeyId"); digestAt(payload.signingKeyId, "delegation.payload.signingKeyId");
  if (payload.rootKeyId !== envelope.keyId || payload.rootKeyId === payload.signingKeyId) fail("IDENTITY_MISMATCH", "Delegation key identities do not match their signing roles.");
  parseP256PublicJwk(payload.publicKeyJwk, "delegation.payload.publicKeyJwk");
  const status = enumAt(payload.status, ["active", "revoked"] as const, "delegation.payload.status");
  const issuedAt = timestampAt(payload.issuedAt, "delegation.payload.issuedAt");
  const notBefore = timestampAt(payload.notBefore, "delegation.payload.notBefore");
  const expiresAt = timestampAt(payload.expiresAt, "delegation.payload.expiresAt");
  if (Date.parse(notBefore) < Date.parse(issuedAt) || Date.parse(expiresAt) <= Date.parse(notBefore)) fail("INVALID_TIME_RANGE", "Delegation validity times are inconsistent.");
  if (status === "active" && payload.revokedAt !== null) fail("INVALID_TIME_RANGE", "An active delegated key cannot have a revocation time.");
  if (status === "revoked") {
    const revokedAt = timestampAt(payload.revokedAt, "delegation.payload.revokedAt");
    if (Date.parse(revokedAt) < Date.parse(issuedAt) || Date.parse(revokedAt) > Date.parse(expiresAt)) fail("INVALID_TIME_RANGE", "Delegated key revocation time is outside its signed validity window.");
  }
  minimumClientVersionAt(payload.minimumClientVersion, "delegation.payload.minimumClientVersion");
  return envelope as unknown as SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload>;
}

export function parseTeamSkillsTrustBootstrap(value: unknown): TeamSkillsTrustBootstrap {
  const bootstrap = recordAt(value, "trustBootstrap");
  const keys = ["schemaVersion", "protocolVersion", "workspaceId", "minimumClientVersion", "delegations"] as const;
  exactKeys(bootstrap, keys, "trustBootstrap"); requiredKeys(bootstrap, keys, "trustBootstrap");
  validateSchema(bootstrap, "trustBootstrap"); validateProtocol(bootstrap.protocolVersion);
  identifierAt(bootstrap.workspaceId, "trustBootstrap.workspaceId");
  minimumClientVersionAt(bootstrap.minimumClientVersion, "trustBootstrap.minimumClientVersion");
  if (!Array.isArray(bootstrap.delegations) || bootstrap.delegations.length < 1 || bootstrap.delegations.length > 4) {
    fail("INVALID_CONTRACT", "Trust bootstrap must contain one to four delegated signing keys.");
  }
  let previous: SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload> | undefined;
  const identities = new Set<string>();
  for (const valueEntry of bootstrap.delegations) {
    const delegation = parseDelegatedSigningKeyEnvelope(valueEntry);
    const identity = `${delegation.payload.signingKeyId}:${delegation.payload.delegationGeneration}`;
    if (identities.has(identity)) fail("INVALID_CONTRACT", "Trust bootstrap contains a duplicate delegated key.");
    if (previous && compareDelegations(previous, delegation) >= 0) {
      fail("INVALID_CONTRACT", "Trust bootstrap delegated keys must use canonical generation and key ordering.");
    }
    identities.add(identity); previous = delegation;
  }
  return bootstrap as unknown as TeamSkillsTrustBootstrap;
}

function compareDelegations(
  left: SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload>,
  right: SignedTeamSkillsEnvelope<DelegatedSigningKeyPayload>,
): number {
  return left.payload.delegationGeneration - right.payload.delegationGeneration
    || compareUtf8(left.payload.signingKeyId, right.payload.signingKeyId);
}

function parseReleaseLikeBase(payload: Record<string, unknown>, path: string): void {
  validateSchema(payload, path, TEAM_SKILLS_RELEASE_SCHEMA_VERSION); validateProtocol(payload.protocolVersion);
  digestAt(payload.signingKeyId, `${path}.signingKeyId`); identifierAt(payload.workspaceId, `${path}.workspaceId`);
  integerAt(payload.feedGeneration, `${path}.feedGeneration`, 1);
  const issuedAt = timestampAt(payload.issuedAt, `${path}.issuedAt`);
  const expiresAt = timestampAt(payload.expiresAt, `${path}.expiresAt`);
  validateTimeRange(issuedAt, expiresAt);
  minimumClientVersionAt(payload.minimumClientVersion, `${path}.minimumClientVersion`);
}

export function parseReleasePayload(value: unknown): TeamSkillReleasePayload {
  const payload = recordAt(value, "release");
  const keys = ["schemaVersion", "protocolVersion", "kind", "signingKeyId", "workspaceId", "skillId", "versionId", "versionNumber", "manifestDigest", "capabilityDigest", "manifest", "capability", "scannerVersion", "releaseGeneration", "feedGeneration", "lifecycle", "locked", "issuedAt", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "release"); requiredKeys(payload, keys, "release"); parseReleaseLikeBase(payload, "release");
  if (payload.kind !== "release") fail("INVALID_CONTRACT", "release.kind must equal release.");
  identifierAt(payload.skillId, "release.skillId"); identifierAt(payload.versionId, "release.versionId");
  integerAt(payload.versionNumber, "release.versionNumber", 1); integerAt(payload.releaseGeneration, "release.releaseGeneration", 1);
  digestAt(payload.manifestDigest, "release.manifestDigest"); digestAt(payload.capabilityDigest, "release.capabilityDigest");
  parseCanonicalSkillManifest(payload.manifest); parseCapabilityEnvelope(payload.capability);
  identifierAt(payload.scannerVersion, "release.scannerVersion");
  if (payload.lifecycle !== "active" || typeof payload.locked !== "boolean") fail("INVALID_CONTRACT", "Release lifecycle or lock state is invalid.");
  if ((payload.capability as TeamSkillCapabilityEnvelope).scannerVersion !== payload.scannerVersion) fail("IDENTITY_MISMATCH", "Release scanner version does not match its capability envelope.");
  return payload as unknown as TeamSkillReleasePayload;
}

export async function validateReleasePayloadBindings(value: TeamSkillReleasePayload): Promise<TeamSkillReleasePayload> {
  const payload = parseReleasePayload(value);
  const manifestPaths = new Set(payload.manifest.files.map((file) => file.path));
  const executablePaths = payload.manifest.files
    .filter((file) => file.mode === "0755")
    .map((file) => file.path);
  if (
    canonicalTeamSkillsJson(executablePaths) !== canonicalTeamSkillsJson(payload.capability.executableFiles)
    || executablePaths.some((path) => !payload.capability.scripts.includes(path))
    || payload.capability.scripts.some((path) => !manifestPaths.has(path))
    || payload.capability.binaryFiles.some((path) => !manifestPaths.has(path))
  ) {
    fail("CAPABILITY_MANIFEST_MISMATCH", "Release capabilities do not match the canonical manifest file modes and paths.");
  }
  const [manifestDigest, capabilityDigest] = await Promise.all([
    digestSkillManifest(payload.manifest),
    digestCapabilityEnvelope(payload.capability),
  ]);
  if (manifestDigest !== payload.manifestDigest || capabilityDigest !== payload.capabilityDigest) {
    fail("IDENTITY_MISMATCH", "Release digests do not match the embedded manifest and capability envelope.");
  }
  return payload;
}

export async function digestReleasePayload(value: TeamSkillReleasePayload): Promise<string> {
  return sha256Canonical(parseReleasePayload(value));
}

export function parseSignedReleaseEnvelope(value: unknown): SignedTeamSkillsEnvelope<TeamSkillReleasePayload> {
  const envelope = parseSignedEnvelopeShape(value, "signedRelease");
  const payload = parseReleasePayload(envelope.payload);
  if (payload.signingKeyId !== envelope.keyId) fail("IDENTITY_MISMATCH", "Release signing key does not match its envelope.");
  return envelope as unknown as SignedTeamSkillsEnvelope<TeamSkillReleasePayload>;
}

export function parseTombstonePayload(value: unknown): TeamSkillTombstonePayload {
  const payload = recordAt(value, "tombstone");
  const keys = ["schemaVersion", "protocolVersion", "kind", "signingKeyId", "workspaceId", "skillId", "releaseGeneration", "feedGeneration", "tombstoneGeneration", "previousManifestDigest", "lifecycle", "locked", "removedAt", "issuedAt", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "tombstone"); requiredKeys(payload, keys, "tombstone"); parseReleaseLikeBase(payload, "tombstone");
  if (payload.kind !== "tombstone") fail("INVALID_CONTRACT", "tombstone.kind must equal tombstone.");
  identifierAt(payload.skillId, "tombstone.skillId"); integerAt(payload.releaseGeneration, "tombstone.releaseGeneration", 1);
  integerAt(payload.tombstoneGeneration, "tombstone.tombstoneGeneration", 1); digestAt(payload.previousManifestDigest, "tombstone.previousManifestDigest");
  if (payload.lifecycle !== "removed" || typeof payload.locked !== "boolean") fail("INVALID_CONTRACT", "Tombstone lifecycle or lock state is invalid.");
  const removedAt = timestampAt(payload.removedAt, "tombstone.removedAt");
  if (Date.parse(removedAt) > Date.parse(payload.issuedAt as string)) fail("INVALID_TIME_RANGE", "Tombstone removal time cannot follow its signed issue time.");
  return payload as unknown as TeamSkillTombstonePayload;
}

export function parseTerminalAuthorityPayload(value: unknown): TeamSkillTerminalAuthorityPayload {
  const payload = recordAt(value, "terminalAuthority");
  const keys = ["schemaVersion", "protocolVersion", "kind", "signingKeyId", "workspaceId", "skillId", "deviceId", "authorityGeneration", "feedGeneration", "reason", "lifecycle", "locked", "issuedAt", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "terminalAuthority"); requiredKeys(payload, keys, "terminalAuthority"); parseReleaseLikeBase(payload, "terminalAuthority");
  if (payload.kind !== "terminal_authority") fail("INVALID_CONTRACT", "terminalAuthority.kind must equal terminal_authority.");
  identifierAt(payload.deviceId, "terminalAuthority.deviceId"); integerAt(payload.authorityGeneration, "terminalAuthority.authorityGeneration", 1);
  const reason = enumAt(payload.reason, ["skill_disabled", "skill_removed", "access_ended", "membership_revoked", "device_revoked"] as const, "terminalAuthority.reason");
  const skillScoped = reason === "skill_disabled" || reason === "skill_removed";
  if ((skillScoped && typeof payload.skillId !== "string") || (!skillScoped && payload.skillId !== null)) fail("IDENTITY_MISMATCH", "Terminal authority scope does not match its reason.");
  if (skillScoped) identifierAt(payload.skillId, "terminalAuthority.skillId");
  const expectedLifecycle = reason === "skill_disabled" ? "disabled" : "removed";
  if (payload.lifecycle !== expectedLifecycle || payload.locked !== true) fail("IDENTITY_MISMATCH", "Terminal authority lifecycle does not match its reason.");
  return payload as unknown as TeamSkillTerminalAuthorityPayload;
}

export function parseStatePayload(value: unknown): TeamSkillStatePayload {
  const payload = recordAt(value, "state");
  const keys = ["schemaVersion", "protocolVersion", "kind", "signingKeyId", "workspaceId", "skillId", "versionId", "manifestDigest", "capabilityDigest", "releaseGeneration", "stateGeneration", "feedGeneration", "lifecycle", "locked", "issuedAt", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "state"); requiredKeys(payload, keys, "state"); parseReleaseLikeBase(payload, "state");
  if (payload.kind !== "state") fail("INVALID_CONTRACT", "state.kind must equal state.");
  identifierAt(payload.skillId, "state.skillId");
  integerAt(payload.releaseGeneration, "state.releaseGeneration", 1);
  integerAt(payload.stateGeneration, "state.stateGeneration", 1);
  enumAt(payload.lifecycle, ["active", "disabled"] as const, "state.lifecycle");
  if (typeof payload.locked !== "boolean") fail("INVALID_CONTRACT", "state.locked must be a boolean.");
  const identityValues = [payload.versionId, payload.manifestDigest, payload.capabilityDigest];
  if (identityValues.every((entry) => entry === null)) return payload as unknown as TeamSkillStatePayload;
  if (typeof payload.versionId !== "string" || typeof payload.manifestDigest !== "string" || typeof payload.capabilityDigest !== "string") {
    fail("IDENTITY_MISMATCH", "State version and digest identity must be wholly present or wholly absent.");
  }
  identifierAt(payload.versionId, "state.versionId"); digestAt(payload.manifestDigest, "state.manifestDigest"); digestAt(payload.capabilityDigest, "state.capabilityDigest");
  return payload as unknown as TeamSkillStatePayload;
}

export function parseFeedCheckpointPayload(value: unknown): TeamSkillsFeedCheckpointPayload {
  const payload = recordAt(value, "feedCheckpoint");
  const keys = ["schemaVersion", "protocolVersion", "kind", "signingKeyId", "workspaceId", "requestedCursor", "fromCursor", "toCursor", "currentCursor", "hasMore", "outcome", "eventDigest", "issuedAt", "expiresAt", "minimumClientVersion"] as const;
  exactKeys(payload, keys, "feedCheckpoint"); requiredKeys(payload, keys, "feedCheckpoint");
  validateSchema(payload, "feedCheckpoint"); validateProtocol(payload.protocolVersion);
  digestAt(payload.signingKeyId, "feedCheckpoint.signingKeyId"); identifierAt(payload.workspaceId, "feedCheckpoint.workspaceId");
  const issuedAt = timestampAt(payload.issuedAt, "feedCheckpoint.issuedAt");
  const expiresAt = timestampAt(payload.expiresAt, "feedCheckpoint.expiresAt");
  validateTimeRange(issuedAt, expiresAt); minimumClientVersionAt(payload.minimumClientVersion, "feedCheckpoint.minimumClientVersion");
  if (payload.kind !== "feed_checkpoint") fail("INVALID_CONTRACT", "feedCheckpoint.kind must equal feed_checkpoint.");
  const requested = integerAt(payload.requestedCursor, "feedCheckpoint.requestedCursor");
  const from = integerAt(payload.fromCursor, "feedCheckpoint.fromCursor");
  const to = integerAt(payload.toCursor, "feedCheckpoint.toCursor");
  const current = integerAt(payload.currentCursor, "feedCheckpoint.currentCursor");
  if (requested !== from || to < from || current < to || typeof payload.hasMore !== "boolean") fail("INVALID_GENERATION", "Feed checkpoint cursor range is invalid.");
  enumAt(payload.outcome, ["events", "cursor_reset_required"] as const, "feedCheckpoint.outcome");
  digestAt(payload.eventDigest, "feedCheckpoint.eventDigest");
  return payload as unknown as TeamSkillsFeedCheckpointPayload;
}

export function parseSignedTombstoneEnvelope(value: unknown): SignedTeamSkillsEnvelope<TeamSkillTombstonePayload> {
  const envelope = parseSignedEnvelopeShape(value, "signedTombstone"); const payload = parseTombstonePayload(envelope.payload);
  if (payload.signingKeyId !== envelope.keyId) fail("IDENTITY_MISMATCH", "Tombstone signing key does not match its envelope.");
  return envelope as unknown as SignedTeamSkillsEnvelope<TeamSkillTombstonePayload>;
}

export function parseSignedTerminalAuthorityEnvelope(value: unknown): SignedTeamSkillsEnvelope<TeamSkillTerminalAuthorityPayload> {
  const envelope = parseSignedEnvelopeShape(value, "signedTerminalAuthority"); const payload = parseTerminalAuthorityPayload(envelope.payload);
  if (payload.signingKeyId !== envelope.keyId) fail("IDENTITY_MISMATCH", "Terminal authority signing key does not match its envelope.");
  return envelope as unknown as SignedTeamSkillsEnvelope<TeamSkillTerminalAuthorityPayload>;
}

export function parseSignedStateEnvelope(value: unknown): SignedTeamSkillsEnvelope<TeamSkillStatePayload> {
  const envelope = parseSignedEnvelopeShape(value, "signedState"); const payload = parseStatePayload(envelope.payload);
  if (payload.signingKeyId !== envelope.keyId) fail("IDENTITY_MISMATCH", "State signing key does not match its envelope.");
  return envelope as unknown as SignedTeamSkillsEnvelope<TeamSkillStatePayload>;
}

export function parseSignedFeedCheckpointEnvelope(value: unknown): SignedTeamSkillsEnvelope<TeamSkillsFeedCheckpointPayload> {
  const envelope = parseSignedEnvelopeShape(value, "signedFeedCheckpoint"); const payload = parseFeedCheckpointPayload(envelope.payload);
  if (payload.signingKeyId !== envelope.keyId) fail("IDENTITY_MISMATCH", "Feed checkpoint signing key does not match its envelope.");
  return envelope as unknown as SignedTeamSkillsEnvelope<TeamSkillsFeedCheckpointPayload>;
}

interface TeamSkillsWorkspaceFeedBase {
  schemaVersion: 1;
  protocolVersion: "v1";
  workspaceId: string;
  requestedCursor: number;
  fromCursor: number;
  toCursor: number;
  currentCursor: number;
  hasMore: boolean;
  minimumClientVersion: string;
  eventDigest: string;
  checkpoint: SignedTeamSkillsEnvelope<TeamSkillsFeedCheckpointPayload>;
}

export type TeamSkillsWorkspaceFeed =
  | TeamSkillsWorkspaceFeedBase & { outcome: "events"; events: Array<{ sequence: number; eventType: "release" | "state" | "tombstone" | "terminal_authority"; envelope: unknown }> }
  | TeamSkillsWorkspaceFeedBase & { outcome: "cursor_reset_required"; reasonCode: "CURSOR_EXPIRED"; snapshot: unknown[] };

function parseFeedEnvelope(eventType: string, value: unknown): { workspaceId: string; feedGeneration: number } {
  if (eventType === "release") return parseSignedReleaseEnvelope(value).payload;
  if (eventType === "state") return parseSignedStateEnvelope(value).payload;
  if (eventType === "tombstone") return parseSignedTombstoneEnvelope(value).payload;
  return parseSignedTerminalAuthorityEnvelope(value).payload;
}

function parseFeedBase(feed: Record<string, unknown>): {
  requested: number;
  from: number;
  to: number;
  current: number;
  checkpoint: SignedTeamSkillsEnvelope<TeamSkillsFeedCheckpointPayload>;
} {
  validateSchema(feed, "feed", TEAM_SKILLS_FEED_SCHEMA_VERSION); validateProtocol(feed.protocolVersion);
  identifierAt(feed.workspaceId, "feed.workspaceId");
  const requested = integerAt(feed.requestedCursor, "feed.requestedCursor");
  const from = integerAt(feed.fromCursor, "feed.fromCursor");
  const to = integerAt(feed.toCursor, "feed.toCursor");
  const current = integerAt(feed.currentCursor, "feed.currentCursor");
  if (requested !== from || to < from || current < to || typeof feed.hasMore !== "boolean") fail("INVALID_GENERATION", "Feed cursor range is invalid.");
  minimumClientVersionAt(feed.minimumClientVersion, "feed.minimumClientVersion"); digestAt(feed.eventDigest, "feed.eventDigest");
  const checkpoint = parseSignedFeedCheckpointEnvelope(feed.checkpoint);
  const boundFields = ["workspaceId", "requestedCursor", "fromCursor", "toCursor", "currentCursor", "hasMore", "outcome", "eventDigest", "minimumClientVersion"] as const;
  for (const field of boundFields) {
    if (checkpoint.payload[field] !== feed[field]) fail(field.includes("Cursor") ? "INVALID_GENERATION" : "IDENTITY_MISMATCH", `Feed checkpoint does not bind ${field}.`);
  }
  return { requested, from, to, current, checkpoint };
}

export function parseWorkspaceFeed(value: unknown): TeamSkillsWorkspaceFeed {
  const feed = recordAt(value, "feed");
  const outcome = enumAt(feed.outcome, ["events", "cursor_reset_required"] as const, "feed.outcome");
  if (outcome === "cursor_reset_required") {
    const keys = ["schemaVersion", "protocolVersion", "outcome", "workspaceId", "requestedCursor", "fromCursor", "toCursor", "currentCursor", "hasMore", "reasonCode", "minimumClientVersion", "eventDigest", "snapshot", "checkpoint"] as const;
    exactKeys(feed, keys, "feed"); requiredKeys(feed, keys, "feed");
    const { to, current } = parseFeedBase(feed);
    if (feed.reasonCode !== "CURSOR_EXPIRED" || !Array.isArray(feed.snapshot)) fail("INVALID_CONTRACT", "Cursor reset feed is invalid.");
    if (feed.hasMore !== false || to !== current) fail("INVALID_GENERATION", "Cursor reset must return one exact snapshot at the current cursor.");
    const seenSkillIds = new Set<string>();
    let previousSkillId: string | undefined;
    for (const entry of feed.snapshot) {
      const envelope = recordAt(entry, "feed.snapshot entry");
      const payload = recordAt(envelope.payload, "feed.snapshot payload");
      const kind = enumAt(payload.kind, ["release", "tombstone", "terminal_authority"] as const, "feed.snapshot payload.kind");
      const parsed = parseFeedEnvelope(kind, entry);
      if (parsed.workspaceId !== feed.workspaceId) fail("IDENTITY_MISMATCH", "Feed snapshot crosses workspace identity.");
      const skillId = identifierAt((parsed as { skillId?: unknown }).skillId, "feed.snapshot skillId");
      if (parsed.feedGeneration > current) fail("INVALID_GENERATION", "Feed snapshot metadata cannot exceed the current cursor.");
      if (seenSkillIds.has(skillId)) fail("DUPLICATE_SKILL_ID", "Feed snapshot contains more than one state for a skill.");
      if (previousSkillId !== undefined && compareUtf8(previousSkillId, skillId) >= 0) {
        fail("INVALID_CONTRACT", "Feed snapshot entries must be strictly sorted by skill ID using UTF-8 bytes.");
      }
      seenSkillIds.add(skillId);
      previousSkillId = skillId;
    }
    return feed as unknown as TeamSkillsWorkspaceFeed;
  }
  const keys = ["schemaVersion", "protocolVersion", "outcome", "workspaceId", "requestedCursor", "fromCursor", "toCursor", "currentCursor", "hasMore", "minimumClientVersion", "eventDigest", "events", "checkpoint"] as const;
  exactKeys(feed, keys, "feed"); requiredKeys(feed, keys, "feed");
  const { from, to, current } = parseFeedBase(feed);
  if (!Array.isArray(feed.events)) fail("INVALID_CONTRACT", "Incremental feed events are invalid.");
  if ((feed.hasMore === true && to >= current) || (feed.hasMore === false && to !== current)) fail("INVALID_GENERATION", "Feed pagination state does not match its cursor range.");
  let previous = from;
  for (const [index, valueEntry] of feed.events.entries()) {
    const entry = recordAt(valueEntry, `feed.events[${index}]`);
    exactKeys(entry, ["sequence", "eventType", "envelope"], `feed.events[${index}]`); requiredKeys(entry, ["sequence", "eventType", "envelope"], `feed.events[${index}]`);
    const sequence = integerAt(entry.sequence, `feed.events[${index}].sequence`, 1);
    if (sequence !== previous + 1 || sequence > to) fail("INVALID_GENERATION", "Feed event sequences must be contiguous within the cursor range.");
    const eventType = enumAt(entry.eventType, ["release", "state", "tombstone", "terminal_authority"] as const, `feed.events[${index}].eventType`);
    const parsed = parseFeedEnvelope(eventType, entry.envelope);
    if (parsed.workspaceId !== feed.workspaceId || parsed.feedGeneration !== sequence) fail("IDENTITY_MISMATCH", "Feed event identity or generation does not match its envelope.");
    previous = sequence;
  }
  if ((feed.events.length === 0 && to !== from) || (feed.events.length > 0 && previous !== to)) {
    fail("INVALID_GENERATION", "Feed cursor must end at the final contiguous event sequence.");
  }
  return feed as unknown as TeamSkillsWorkspaceFeed;
}

export async function verifyWorkspaceFeed(value: unknown): Promise<TeamSkillsWorkspaceFeed> {
  const feed = parseWorkspaceFeed(value);
  const collection = feed.outcome === "events" ? feed.events : feed.snapshot;
  if (await digestTeamSkillsFeedEntries(collection) !== feed.eventDigest) {
    fail("IDENTITY_MISMATCH", "Feed collection does not match its checkpoint-bound digest.");
  }
  return feed;
}

export function canonicalTeamSkillsSignatureInput(value: TeamSkillsSignablePayload): Uint8Array {
  const payload = recordAt(value, "signaturePayload");
  const kind = enumAt(payload.kind, ["delegated_key", "release", "state", "tombstone", "terminal_authority", "feed_checkpoint"] as const, "signaturePayload.kind");
  if (kind === "release") parseReleasePayload(payload);
  else if (kind === "state") parseStatePayload(payload);
  else if (kind === "tombstone") parseTombstonePayload(payload);
  else if (kind === "terminal_authority") parseTerminalAuthorityPayload(payload);
  else if (kind === "feed_checkpoint") parseFeedCheckpointPayload(payload);
  return encoder.encode(`${TEAM_SKILLS_SIGNATURE_DOMAIN}\0${kind}\0${canonicalTeamSkillsJson(payload)}`);
}

export async function digestTeamSkillsFeedEntries(value: readonly unknown[]): Promise<string> {
  return sha256Canonical(value);
}

export type TeamSkillInstallDecision = Record<string, unknown> & { schemaVersion: 1; protocolVersion: "v1"; outcome: string; workspaceId: string; skillId: string };

export function parseInstallDecision(value: unknown): TeamSkillInstallDecision {
  const decision = recordAt(value, "installDecision");
  validateSchema(decision, "installDecision", TEAM_SKILLS_INSTALL_DECISION_SCHEMA_VERSION); validateProtocol(decision.protocolVersion);
  identifierAt(decision.workspaceId, "installDecision.workspaceId"); identifierAt(decision.skillId, "installDecision.skillId");
  const outcome = enumAt(decision.outcome, ["install_release", "no_change", "preserve_local_draft", "collision", "quarantine_clean", "detach_local_draft", "pause"] as const, "installDecision.outcome");
  const base = ["schemaVersion", "protocolVersion", "outcome", "workspaceId", "skillId"];
  if (outcome === "pause") {
    exactKeys(decision, [...base, "reasonCode"], "installDecision"); requiredKeys(decision, [...base, "reasonCode"], "installDecision");
    enumAt(decision.reasonCode, ["OFFLINE", "TRANSIENT_AUTH_FAILURE", "SERVICE_UNAVAILABLE", "UNSUPPORTED_SCHEMA", "ROLLBACK_DETECTED", "EXPIRED_METADATA", "UNTRUSTED_KEY", "IDENTITY_MISMATCH"] as const, "installDecision.reasonCode");
  } else if (outcome === "install_release") {
    const fields = [...base, "versionId", "manifestDigest", "capabilityDigest", "releaseGeneration", "feedGeneration"];
    exactKeys(decision, fields, "installDecision"); requiredKeys(decision, fields, "installDecision");
    identifierAt(decision.versionId, "installDecision.versionId"); digestAt(decision.manifestDigest, "installDecision.manifestDigest"); digestAt(decision.capabilityDigest, "installDecision.capabilityDigest");
    integerAt(decision.releaseGeneration, "installDecision.releaseGeneration", 1); integerAt(decision.feedGeneration, "installDecision.feedGeneration", 1);
  } else if (outcome === "quarantine_clean" || outcome === "detach_local_draft") {
    const fields = [...base, "reason", "authorityGeneration", "signedMetadataDigest"];
    exactKeys(decision, fields, "installDecision"); requiredKeys(decision, fields, "installDecision");
    enumAt(decision.reason, ["skill_disabled", "skill_removed", "access_ended", "membership_revoked", "device_revoked"] as const, "installDecision.reason");
    integerAt(decision.authorityGeneration, "installDecision.authorityGeneration", 1); digestAt(decision.signedMetadataDigest, "installDecision.signedMetadataDigest");
  } else if (outcome === "preserve_local_draft") {
    exactKeys(decision, [...base, "reasonCode", "incomingVersionId"], "installDecision"); requiredKeys(decision, [...base, "reasonCode", "incomingVersionId"], "installDecision");
    if (decision.reasonCode !== "LOCAL_MODIFICATIONS") fail("INVALID_CONTRACT", "Preserve decision must describe local modifications.");
    identifierAt(decision.incomingVersionId, "installDecision.incomingVersionId");
  } else if (outcome === "collision") {
    exactKeys(decision, [...base, "reasonCode"], "installDecision"); requiredKeys(decision, [...base, "reasonCode"], "installDecision");
    if (decision.reasonCode !== "UNMANAGED_DESTINATION_COLLISION") fail("INVALID_CONTRACT", "Collision decision must describe the unmanaged destination.");
  } else {
    exactKeys(decision, base, "installDecision"); requiredKeys(decision, base, "installDecision");
  }
  return decision as TeamSkillInstallDecision;
}

export function supportsTeamSkillsProtocol(value: unknown): value is typeof TEAM_SKILLS_PROTOCOL_VERSION {
  return value === TEAM_SKILLS_PROTOCOL_VERSION;
}

export function teamSkillsProtocolCompatibility(value: unknown): TeamSkillsProtocolCompatibility {
  if (value === TEAM_SKILLS_PROTOCOL_VERSION) return { outcome: "compatible", requestedProtocolVersion: value, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION };
  if (value === TEAM_SKILLS_LEGACY_PROTOCOL_VERSION) return { outcome: "upgrade_required", requestedProtocolVersion: value, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION, reasonCode: "DRAFT_PROTOCOL_RETIRED" };
  return { outcome: "unsupported", requestedProtocolVersion: typeof value === "string" ? value : null, protocolVersion: TEAM_SKILLS_PROTOCOL_VERSION, reasonCode: "UNSUPPORTED_PROTOCOL_VERSION" };
}
